import * as THREE from 'three';
import { EnvironmentSystemBase } from '../EnvironmentSystemBase';
import { SunConfig } from '../types';

const DEFAULTS: SunConfig = {
    intensity: 1.0,
    color: 0xFDB813,
    raysCount: 8,
    cloudHeightRef: 25,
    animate: true
};

export class SunSystem extends EnvironmentSystemBase {
    public config: SunConfig;
    private sunMesh: THREE.Mesh | null = null;
    private sunRays: THREE.Group | null = null;
    private light: THREE.DirectionalLight | null = null;

    constructor(name: string, cfg?: Partial<SunConfig>) {
        super(name);
        this.config = { ...DEFAULTS, ...(cfg || {}) };
    }

    setConfig(cfg: Partial<SunConfig>): void {
        this.config = { ...this.config, ...cfg };
        this.rebuild();
    }

    setIntensity(i: number): void {
        this.config.intensity = i;
        if (this.light) this.light.intensity = i;
    }

    protected build(): void {
        this.group.clear();
        // light
        this.light = new THREE.DirectionalLight(0xffffff, this.config.intensity);
        this.light.position.set(30, this.config.cloudHeightRef + 20, 0);
        this.group.add(this.light);
        // sun mesh
        const sunGeo = new THREE.SphereGeometry(3, 16, 16);
        const sunMat = new THREE.MeshBasicMaterial({ color: this.config.color, transparent: true, opacity: 0.9 });
        this.sunMesh = new THREE.Mesh(sunGeo, sunMat);
        this.sunMesh.position.set(30, this.config.cloudHeightRef + 20, 0);
        this.group.add(this.sunMesh);
        // rays (simple cones)
        this.sunRays = new THREE.Group();
        for (let i = 0; i < this.config.raysCount; i++) {
            const len = 50 + Math.random() * 30;
            const rayGeo = new THREE.ConeGeometry(0.5, len, 8, 1, true);
            const rayMat = new THREE.MeshBasicMaterial({ color: 0xFFF8DC, transparent: true, opacity: 0.15, side: THREE.DoubleSide });
            const ray = new THREE.Mesh(rayGeo, rayMat);
            ray.position.copy(this.sunMesh.position);
            ray.position.y -= len / 2;
            ray.rotation.x = Math.PI;
            ray.rotation.y = (i / this.config.raysCount) * Math.PI * 2;
            ray.rotation.z = (Math.random() - 0.5) * 0.3;
            this.sunRays.add(ray);
        }
        this.group.add(this.sunRays);
    }

    private rebuild(): void {
        this.build();
    }

    protected onUpdate(dt: number): void {
        if (!this.enabled || !this.config.animate) return;
        const t = performance.now() * 0.001;
        if (this.sunMesh) {
            this.sunMesh.position.x = 30 + Math.sin(t * 0.1) * 2;
            this.sunMesh.position.z = Math.cos(t * 0.1) * 1;
            const mat = this.sunMesh.material as THREE.MeshBasicMaterial;
            mat.opacity = 0.7 + Math.sin(t * 3) * 0.2;
        }
        if (this.light && this.sunMesh) {
            this.light.position.copy(this.sunMesh.position);
            this.light.color.setHex(0xFFF8DC);
        }
        if (this.sunRays) {
            this.sunRays.children.forEach((ray: any, idx: number) => {
                const sway = Math.sin(t * 0.5 + idx) * 0.02;
                ray.rotation.z += sway * 0.1;
                if (ray.material) {
                    const base = ray.material.userData.baseOpacity || ray.material.opacity;
                    if (!ray.material.userData.baseOpacity) ray.material.userData.baseOpacity = base;
                    const pulse = 0.7 + Math.sin(t * 2 + idx) * 0.3;
                    ray.material.opacity = base * pulse * this.config.intensity;
                }
            });
        }
    }
}
